import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  getContract,
  updateContract,
  getGpos
} from '../../config/api';
import { 
  Save, 
  X, 
  ArrowLeft, 
  FileText, 
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const EditContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  console.log('🚀 EditContract Component Rendered:', { id, user, location: window.location.href });

  // State for form data
  const [formData, setFormData] = useState({
    number: '',
    description: '',
    type: 'INDIRECT',
    status: 'DRAFT',
    class: '',
    group: '',
    priority: '',
    classOfTrade: '',
    effectiveFrom: '',
    effectiveTo: '',
    gpoId: '',
    contractName: '',
    contractVersion: '',
    renewalDate: '',
    terminationDate: '',
    autoRenewal: false,
    baseDiscount: '',
    volumeDiscount: '',
    promptPayDiscount: '',
    adminFee: '',
    chargebackFee: '',
    paymentTerms: '',
    creditLimit: '',
    minimumOrder: '',
    maximumOrder: '',
    ediEnabled: true,
    ediFormat: '',
    ediVersion: '',
    ediSenderId: '',
    ediReceiverId: '',
    complianceRequired: false,
    auditRequired: false,
    reportingRequired: false,
    notes: ''
  });

  // State for validation errors
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch contract data
  const { data: contract, isLoading: contractLoading, error: contractError } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => getContract(id),
    enabled: !!id && !!user,
    retry: false
  });

  // GPOs for the association dropdown
  const { data: gposData } = useQuery({
    queryKey: ['gpos'],
    queryFn: () => getGpos({ limit: 100, sortBy: 'name', sortOrder: 'asc' }),
    retry: 1
  });

  console.log('🔍 EditContract Debug:', { id, user, contract, contractLoading, contractError });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateContract,
    onSuccess: () => {
      toast.success('Contract updated successfully');
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      navigate('/app/contracts');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error updating contract');
      setIsSubmitting(false);
    }
  });

  // Populate form when contract data loads
  useEffect(() => {
    if (contract) {
      console.log('🔍 Contract data loaded:', contract);
      setFormData({
        number: contract.number || '',
        description: contract.description || '',
        type: contract.type || 'INDIRECT',
        status: contract.status || 'DRAFT',
        class: contract.class || '',
        group: contract.group || '',
        priority: contract.priority?.toString() || '',
        classOfTrade: contract.classOfTrade || '',
        effectiveFrom: contract.effectiveFrom ? new Date(contract.effectiveFrom).toISOString().split('T')[0] : '',
        effectiveTo: contract.effectiveTo ? new Date(contract.effectiveTo).toISOString().split('T')[0] : '',
        gpoId: contract.gpoId || '',
        contractName: contract.contractName || '',
        contractVersion: contract.contractVersion || '',
        renewalDate: contract.renewalDate ? new Date(contract.renewalDate).toISOString().split('T')[0] : '',
        terminationDate: contract.terminationDate ? new Date(contract.terminationDate).toISOString().split('T')[0] : '',
        autoRenewal: !!contract.autoRenewal,
        baseDiscount: contract.baseDiscount ?? '',
        volumeDiscount: contract.volumeDiscount ?? '',
        promptPayDiscount: contract.promptPayDiscount ?? '',
        adminFee: contract.adminFee ?? '',
        chargebackFee: contract.chargebackFee ?? '',
        paymentTerms: contract.paymentTerms || '',
        creditLimit: contract.creditLimit ?? '',
        minimumOrder: contract.minimumOrder ?? '',
        maximumOrder: contract.maximumOrder ?? '',
        ediEnabled: contract.ediEnabled !== false,
        ediFormat: contract.ediFormat || '',
        ediVersion: contract.ediVersion || '',
        ediSenderId: contract.ediSenderId || '',
        ediReceiverId: contract.ediReceiverId || '',
        complianceRequired: !!contract.complianceRequired,
        auditRequired: !!contract.auditRequired,
        reportingRequired: !!contract.reportingRequired,
        notes: contract.notes || ''
      });
    }
  }, [contract]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear validation error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.number.trim()) {
      newErrors.number = 'Contract number is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.effectiveFrom) {
      newErrors.effectiveFrom = 'Effective from date is required';
    }
    if (formData.effectiveFrom && formData.effectiveTo && 
        new Date(formData.effectiveFrom) >= new Date(formData.effectiveTo)) {
      newErrors.effectiveTo = 'Effective to date must be after effective from date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Prepare data for submission
    const submitData = { ...formData };
    
    // Convert empty strings to null for optional fields
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '') {
        submitData[key] = null;
      }
    });

    // Convert priority to integer if provided
    if (submitData.priority) {
      submitData.priority = parseInt(submitData.priority);
    }

    // Convert date strings to Date objects
    if (submitData.effectiveFrom) {
      submitData.effectiveFrom = new Date(submitData.effectiveFrom);
    }
    if (submitData.effectiveTo) {
      submitData.effectiveTo = new Date(submitData.effectiveTo);
    }

    updateMutation.mutate({ id, contractData: submitData });
  };

  // Role checking is now handled by ProtectedRoute component
  // No need to check here

  if (contractLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (contractError) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/contracts')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            title="Back to Contracts"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Contract</h1>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Contract</h2>
          <p className="text-gray-500 mb-4">{contractError.message || 'Failed to load contract details'}</p>
          <button
            onClick={() => navigate('/app/contracts')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              console.log('🔙 Header back button clicked, navigating to /contracts');
              console.log('📍 Current location:', window.location.href);
              navigate('/app/contracts', { replace: true });
            }}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            title="Back to Contracts"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Contract</h1>
            <p className="text-gray-600">Update contract information</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="h-8 w-8 text-primary-600" />
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Contract Information</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contract Number *
              </label>
              <input
                type="text"
                name="number"
                value={formData.number}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.number ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter contract number"
              />
              {errors.number && (
                <p className="mt-1 text-sm text-red-600">{errors.number}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter contract description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contract Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="INDIRECT">Indirect</option>
                <option value="DIRECT">Direct</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>

          {/* GPO association */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Purchasing Organization (GPO)
              </label>
              <select
                name="gpoId"
                value={formData.gpoId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">No GPO (direct contract)</option>
                {gposData?.data?.gpos?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.city && g.state ? ` (${g.city}, ${g.state})` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">Change the GPO that administers this contract.</p>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class
              </label>
              <input
                type="text"
                name="class"
                value={formData.class}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., WHOLESALE, HOSPITAL"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group
              </label>
              <input
                type="text"
                name="group"
                value={formData.group}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Q1-2024, DIRECT-2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="1, 2, 3..."
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class of Trade
              </label>
              <select
                name="classOfTrade"
                value={formData.classOfTrade}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select Class of Trade</option>
                <option value="RETAIL_PHARMACY">Retail Pharmacy</option>
                <option value="HOSPITAL">Hospital</option>
                <option value="LONG_TERM_CARE">Long-term Care Facility</option>
                <option value="SPECIALTY_PHARMACY">Specialty Pharmacy</option>
                <option value="WHOLESALER">Wholesaler/Distributor</option>
                <option value="GOVERNMENT_FACILITY">Government Facility</option>
              </select>
            </div>
          </div>

          {/* Effective Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective From *
              </label>
              <input
                type="date"
                name="effectiveFrom"
                value={formData.effectiveFrom}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.effectiveFrom ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.effectiveFrom && (
                <p className="mt-1 text-sm text-red-600">{errors.effectiveFrom}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective To
              </label>
              <input
                type="date"
                name="effectiveTo"
                value={formData.effectiveTo}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.effectiveTo ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank for an open-ended (evergreen) contract.</p>
              {errors.effectiveTo && (
                <p className="mt-1 text-sm text-red-600">{errors.effectiveTo}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contract Terms */}
        <div className="p-6 border-t border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Terms</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Name</label>
              <input type="text" name="contractName" value={formData.contractName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
              <input type="text" name="contractVersion" value={formData.contractVersion} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
              <input type="text" name="paymentTerms" value={formData.paymentTerms} onChange={handleInputChange} placeholder="Net 30" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Renewal Date</label>
              <input type="date" name="renewalDate" value={formData.renewalDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Termination Date</label>
              <input type="date" name="terminationDate" value={formData.terminationDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Discount %</label>
              <input type="number" name="baseDiscount" value={formData.baseDiscount} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Volume Discount %</label>
              <input type="number" name="volumeDiscount" value={formData.volumeDiscount} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Pay Discount %</label>
              <input type="number" name="promptPayDiscount" value={formData.promptPayDiscount} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Fee (%)</label>
              <div className="relative">
                <input type="number" name="adminFee" value={formData.adminFee} onChange={handleInputChange} step="0.01" min="0" max="100" placeholder="e.g. 3.00" className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chargeback Fee (%)</label>
              <div className="relative">
                <input type="number" name="chargebackFee" value={formData.chargebackFee} onChange={handleInputChange} step="0.01" min="0" max="100" placeholder="e.g. 2.00" className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Credit Limit</label>
              <input type="number" name="creditLimit" value={formData.creditLimit} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Order</label>
              <input type="number" name="minimumOrder" value={formData.minimumOrder} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Order</label>
              <input type="number" name="maximumOrder" value={formData.maximumOrder} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EDI Format</label>
              <input type="text" name="ediFormat" value={formData.ediFormat} onChange={handleInputChange} placeholder="X12" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EDI Version</label>
              <input type="text" name="ediVersion" value={formData.ediVersion} onChange={handleInputChange} placeholder="5010" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EDI Sender ID</label>
              <input type="text" name="ediSenderId" value={formData.ediSenderId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EDI Receiver ID</label>
              <input type="text" name="ediReceiverId" value={formData.ediReceiverId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="autoRenewal" checked={formData.autoRenewal} onChange={handleInputChange} className="rounded border-gray-300" />
              Auto Renewal
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="ediEnabled" checked={formData.ediEnabled} onChange={handleInputChange} className="rounded border-gray-300" />
              EDI Enabled
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="complianceRequired" checked={formData.complianceRequired} onChange={handleInputChange} className="rounded border-gray-300" />
              Compliance Required
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="auditRequired" checked={formData.auditRequired} onChange={handleInputChange} className="rounded border-gray-300" />
              Audit Required
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="reportingRequired" checked={formData.reportingRequired} onChange={handleInputChange} className="rounded border-gray-300" />
              Reporting Required
            </label>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              console.log('❌ Cancel button clicked, navigating to /contracts');
              console.log('📍 Current location:', window.location.href);
              navigate('/app/contracts', { replace: true });
            }}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-transparent flex items-center gap-2"
            title="Cancel"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Save Changes"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditContract;
